import React, { useState, useEffect } from 'react';
import { X, Plus, Save, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import evidenceAPI from '../../services/evidenceAPI';
import type { EvidenceSourceType } from '../../types/evidence';

interface AddSourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (sourceId?: number, shouldSync?: boolean) => void;
  editSource?: any; // Source to edit (if editing)
}

const AddSourceModal: React.FC<AddSourceModalProps> = ({ isOpen, onClose, onSuccess, editSource }) => {
  const [sourceName, setSourceName] = useState('');
  const [sourceType, setSourceType] = useState<EvidenceSourceType>('github');
  const [config, setConfig] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'success' | 'error' | null>(null);
  const [connectionMessage, setConnectionMessage] = useState('');

  const isEditMode = !!editSource;

  // Load edit data when modal opens
  useEffect(() => {
    if (editSource && isOpen) {
      setSourceName(editSource.source_name || '');
      setSourceType(editSource.source_type || 'github');
      setConfig(editSource.connection_config || {});
    } else if (!isOpen) {
      resetForm();
    }
  }, [editSource, isOpen]);

  const sourceTypes: { value: EvidenceSourceType; label: string; fields: string[] }[] = [
    {
      value: 'github',
      label: 'GitHub',
      fields: ['owner', 'repo', 'token']
    },
    {
      value: 'gitlab',
      label: 'GitLab',
      fields: ['url', 'project_id', 'token']
    },
    {
      value: 'ssl_labs',
      label: 'SSL Labs',
      fields: ['hostname']
    },
    {
      value: 'aws_cloud',
      label: 'AWS Cloud',
      fields: ['region', 'access_key', 'secret_key']
    },
    {
      value: 'siem_splunk',
      label: 'Splunk SIEM',
      fields: ['base_url', 'api_token', 'search_query']
    },
    {
      value: 'siem_elastic',
      label: 'Elasticsearch',
      fields: ['host', 'api_key', 'index']
    },
    {
      value: 'manual_upload',
      label: 'Manual Upload',
      fields: []
    }
  ];

  const currentSourceType = sourceTypes.find(st => st.value === sourceType);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const sourceData = {
        source_name: sourceName,
        source_type: sourceType,
        connection_config: config,
        collection_frequency: 'daily' as const,
        auto_sync_enabled: true,
        regulatory_frameworks: ['SOC2', 'ISO27001', 'PCI_DSS'],
        compliance_tags: [],
        staleness_threshold_days: 30
      };

      let sourceId: number;

      if (isEditMode) {
        // Update existing source
        const updated = await evidenceAPI.updateEvidenceSource(editSource.id, sourceData);
        sourceId = updated.id;
      } else {
        // Create new source
        const created = await evidenceAPI.createEvidenceSource(sourceData);
        sourceId = created.id;
      }

      // Auto-sync/scan after save
      onSuccess(sourceId, true);
      onClose();
      resetForm();
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} evidence source`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSourceName('');
    setSourceType('github');
    setConfig({});
    setError('');
    setConnectionStatus(null);
    setConnectionMessage('');
  };

  const handleConfigChange = (field: string, value: string) => {
    setConfig(prev => ({ ...prev, [field]: value }));
    // Reset connection status when config changes
    setConnectionStatus(null);
    setConnectionMessage('');
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    setConnectionStatus(null);
    setConnectionMessage('');
    setError('');

    try {
      const result = await evidenceAPI.testConnection(sourceType, config);

      if (result.success) {
        setConnectionStatus('success');
        setConnectionMessage(result.message || 'Connection successful!');
      } else {
        setConnectionStatus('error');
        setConnectionMessage(result.error || 'Connection failed');
      }
    } catch (err: any) {
      setConnectionStatus('error');
      setConnectionMessage(err.response?.data?.message || err.message || 'Failed to test connection');
    } finally {
      setTestingConnection(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-800">
            {isEditMode ? 'Edit Evidence Source' : 'Add Evidence Source'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Source Name
            </label>
            <input
              type="text"
              value={sourceName}
              onChange={(e) => setSourceName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., GitHub Main Repository"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Source Type
            </label>
            <select
              value={sourceType}
              onChange={(e) => {
                setSourceType(e.target.value as EvidenceSourceType);
                setConfig({});
                setConnectionStatus(null);
                setConnectionMessage('');
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isEditMode} // Can't change type when editing
            >
              {sourceTypes.map(st => (
                <option key={st.value} value={st.value}>
                  {st.label}
                </option>
              ))}
            </select>
            {isEditMode && (
              <p className="text-xs text-gray-500 mt-1">Source type cannot be changed after creation</p>
            )}
          </div>

          {currentSourceType && currentSourceType.fields.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-700">Connection Configuration</h3>

              {currentSourceType.fields.map(field => (
                <div key={field}>
                  <label className="block text-sm text-gray-600 mb-1 capitalize">
                    {field.replace(/_/g, ' ')}
                  </label>
                  <input
                    type={field.includes('token') || field.includes('key') || field.includes('secret') ? 'password' : 'text'}
                    value={config[field] || ''}
                    onChange={(e) => handleConfigChange(field, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={`Enter ${field.replace(/_/g, ' ')}`}
                    required
                  />
                </div>
              ))}

              {sourceType === 'github' && !isEditMode && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                  <strong>GitHub Token:</strong> Use the token from .env file: Github-Monitoring-TSG0
                </div>
              )}

              {/* Test Connection Button */}
              <div className="pt-2">
                <button
                  type="button"
                  onClick={handleTestConnection}
                  disabled={testingConnection || !currentSourceType?.fields.every(f => config[f])}
                  className="w-full px-4 py-2 border border-blue-300 text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {testingConnection ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Testing Connection...
                    </>
                  ) : (
                    <>
                      {connectionStatus === 'success' ? (
                        <CheckCircle className="w-4 h-4" />
                      ) : connectionStatus === 'error' ? (
                        <XCircle className="w-4 h-4" />
                      ) : null}
                      Test Connection
                    </>
                  )}
                </button>

                {/* Connection Status Message */}
                {connectionStatus && (
                  <div className={`mt-2 p-3 rounded-lg text-sm ${
                    connectionStatus === 'success'
                      ? 'bg-green-50 border border-green-200 text-green-800'
                      : 'bg-red-50 border border-red-200 text-red-800'
                  }`}>
                    {connectionStatus === 'success' ? (
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 flex-shrink-0" />
                        <span>{connectionMessage}</span>
                      </div>
                    ) : (
                      <div className="flex items-start gap-2">
                        <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <div>
                          <strong>Connection Failed:</strong> {connectionMessage}
                          <div className="mt-1 text-xs">
                            Please check your credentials and try again.
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {isEditMode ? 'Saving...' : 'Creating...'}
                </>
              ) : (
                <>
                  {isEditMode ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                  {isEditMode ? 'Save & Re-Sync' : 'Add Source'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddSourceModal;
